import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { ArrowRight, Loader2, MousePointerClick, ArrowDown, Clock, Monitor, Smartphone, Globe } from "lucide-react";

interface PopupContent {
  readonly headline?: string;
  readonly body?: string;
  readonly ctaText?: string;
  readonly ctaUrl?: string;
  readonly imageUrl?: string;
  readonly bgColor?: string;
}

interface DisplayRules {
  readonly frequency?: string;
  readonly pages?: readonly string[];
  readonly devices?: string;
  readonly hideForLoggedIn?: boolean;
}

interface PopupData {
  readonly id?: string;
  readonly name: string;
  readonly trigger_type: string;
  readonly trigger_value: string | null;
  readonly content_json: string;
  readonly display_rules_json: string | null;
  readonly is_active: number;
  readonly starts_at: string | null;
  readonly expires_at: string | null;
}

type TriggerType = "exit_intent" | "scroll" | "timed";
type Frequency = "once_session" | "once_day" | "once_ever" | "always";
type DeviceTarget = "all" | "mobile" | "desktop";

const TRIGGER_OPTIONS: readonly { value: TriggerType; label: string; desc: string; icon: ReactNode }[] = [
  { value: "exit_intent", label: "יציאה מהדף",  desc: "כשהעכבר עוזב את הדף", icon: <MousePointerClick className="h-5 w-5" /> },
  { value: "scroll",      label: "גלילה",         desc: "אחוז גלילה מסוים",   icon: <ArrowDown className="h-5 w-5" /> },
  { value: "timed",       label: "זמן",            desc: "אחרי מספר שניות",   icon: <Clock className="h-5 w-5" /> },
];

const FREQUENCY_OPTIONS: readonly { value: Frequency; label: string }[] = [
  { value: "once_session", label: "פעם בסשן" },
  { value: "once_day",     label: "פעם ביום" },
  { value: "once_ever",    label: "פעם אחת" },
  { value: "always",       label: "תמיד" },
];

const DEVICE_OPTIONS: readonly { value: DeviceTarget; label: string; icon: ReactNode }[] = [
  { value: "all",     label: "הכל",          icon: <Globe className="h-4 w-4" /> },
  { value: "mobile",  label: "נייד בלבד",   icon: <Smartphone className="h-4 w-4" /> },
  { value: "desktop", label: "מחשב בלבד",  icon: <Monitor className="h-4 w-4" /> },
];

function FormField({ label, hint, children }: { readonly label: string; readonly hint?: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium leading-none">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function PopupFormPage(): ReactNode {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { accessToken } = useAuth();
  const isNew = !id || id === "new";

  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState<TriggerType>("exit_intent");
  const [triggerValue, setTriggerValue] = useState("");
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [frequency, setFrequency] = useState<Frequency>("once_session");
  const [devices, setDevices] = useState<DeviceTarget>("all");
  const [hideForLoggedIn, setHideForLoggedIn] = useState(false);
  const [startsAt, setStartsAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    void (async () => {
      const res = await fetch(`/api/popups/admin`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: readonly PopupData[] };
      if (!json.success) return;
      const popup = json.data.find((p) => p.id === id);
      if (!popup) return;

      setName(popup.name);
      setTriggerType(popup.trigger_type as TriggerType);
      setTriggerValue(popup.trigger_value ?? "");

      const content: PopupContent = JSON.parse(popup.content_json);
      setHeadline(content.headline ?? "");
      setBody(content.body ?? "");
      setCtaText(content.ctaText ?? "");
      setCtaUrl(content.ctaUrl ?? "");
      setImageUrl(content.imageUrl ?? "");
      setBgColor(content.bgColor ?? "#ffffff");

      if (popup.display_rules_json) {
        const rules: DisplayRules = JSON.parse(popup.display_rules_json);
        setFrequency((rules.frequency as Frequency) ?? "once_session");
        setDevices((rules.devices as DeviceTarget) ?? "all");
        setHideForLoggedIn(rules.hideForLoggedIn ?? false);
      }

      setStartsAt(popup.starts_at ?? "");
      setExpiresAt(popup.expires_at ?? "");
    })();
  }, [id, isNew, accessToken]);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        triggerType,
        triggerValue: triggerValue || undefined,
        content: {
          headline: headline || undefined,
          body: body || undefined,
          ctaText: ctaText || undefined,
          ctaUrl: ctaUrl || undefined,
          imageUrl: imageUrl || undefined,
          bgColor,
        },
        displayRules: { frequency, devices, hideForLoggedIn },
        startsAt: startsAt || undefined,
        expiresAt: expiresAt || undefined,
      };

      const url = isNew ? "/api/popups/admin" : `/api/popups/admin/${id}`;
      const method = isNew ? "POST" : "PUT";

      await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      navigate("/popups");
    } finally {
      setSaving(false);
    }
  }, [name, triggerType, triggerValue, headline, body, ctaText, ctaUrl, imageUrl, bgColor, frequency, devices, hideForLoggedIn, startsAt, expiresAt, isNew, id, accessToken, navigate]);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="min-h-[38px]" onClick={() => navigate("/popups")}>
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isNew ? "פופאפ חדש" : "עריכת פופאפ"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isNew ? "צור חלון קופץ חדש" : "ערוך את הגדרות הפופאפ"}
          </p>
        </div>
      </div>

      {/* Name */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">פרטים בסיסיים</CardTitle>
        </CardHeader>
        <CardContent>
          <FormField label="שם הפופאפ">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="min-h-[44px]"
              placeholder="למשל: הנחה לביקור ראשון"
            />
          </FormField>
        </CardContent>
      </Card>

      {/* Trigger */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">סוג הפעלה</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {TRIGGER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => { setTriggerType(opt.value); setTriggerValue(""); }}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-all min-h-[88px]",
                  triggerType === opt.value
                    ? "border-primary bg-primary/8 shadow-sm"
                    : "hover:bg-muted/40 hover:border-muted-foreground/30"
                )}
              >
                <span className={cn(triggerType === opt.value ? "text-primary" : "text-muted-foreground")}>
                  {opt.icon}
                </span>
                <div>
                  <p className={cn("text-sm font-semibold", triggerType === opt.value ? "text-primary" : "")}>{opt.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                </div>
              </button>
            ))}
          </div>

          {triggerType === "scroll" && (
            <FormField label="אחוז גלילה (%)">
              <Input
                type="number"
                min="0"
                max="100"
                value={triggerValue}
                onChange={(e) => setTriggerValue(e.target.value)}
                placeholder="50"
                className="min-h-[44px] max-w-[140px]"
              />
            </FormField>
          )}

          {triggerType === "timed" && (
            <FormField label="שניות המתנה">
              <Input
                type="number"
                min="1"
                max="120"
                value={triggerValue ? String(Number(triggerValue) / 1000) : ""}
                onChange={(e) => setTriggerValue(String(Number(e.target.value) * 1000))}
                placeholder="5"
                className="min-h-[44px] max-w-[140px]"
              />
            </FormField>
          )}
        </CardContent>
      </Card>

      {/* Content */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">תוכן</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField label="כותרת">
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} className="min-h-[44px]" placeholder="הנחה בלעדית רק בשבילך!" />
          </FormField>
          <FormField label="טקסט">
            <textarea
              value={body}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)}
              rows={3}
              placeholder="תיאור קצר..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="טקסט כפתור">
              <Input value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="לחץ כאן" className="min-h-[44px]" />
            </FormField>
            <FormField label="קישור כפתור">
              <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="https://..." className="min-h-[44px]" dir="ltr" />
            </FormField>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="קישור תמונה">
              <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="/api/images/..." className="min-h-[44px]" dir="ltr" />
            </FormField>
            <FormField label="צבע רקע">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="h-[44px] w-16 rounded-md border cursor-pointer"
                />
                <span className="text-sm font-mono text-muted-foreground">{bgColor}</span>
              </div>
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* Display rules */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">כללי תצוגה</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-sm font-medium mb-2">תדירות</p>
            <div className="flex flex-wrap gap-2">
              {FREQUENCY_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={frequency === opt.value ? "default" : "outline"}
                  size="sm"
                  className="min-h-[44px]"
                  onClick={() => setFrequency(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">מכשירים</p>
            <div className="flex flex-wrap gap-2">
              {DEVICE_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={devices === opt.value ? "default" : "outline"}
                  size="sm"
                  className="min-h-[44px] gap-1.5"
                  onClick={() => setDevices(opt.value)}
                >
                  {opt.icon}
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
            <input
              type="checkbox"
              checked={hideForLoggedIn}
              onChange={(e) => setHideForLoggedIn(e.target.checked)}
              className="h-4 w-4 rounded"
            />
            <span className="text-sm">הסתר למשתמשים מחוברים</span>
          </label>
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">תזמון</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="מתאריך">
            <Input
              type="datetime-local"
              value={startsAt ? startsAt.slice(0, 16) : ""}
              onChange={(e) => setStartsAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="min-h-[44px]"
            />
          </FormField>
          <FormField label="עד תאריך">
            <Input
              type="datetime-local"
              value={expiresAt ? expiresAt.slice(0, 16) : ""}
              onChange={(e) => setExpiresAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="min-h-[44px]"
            />
          </FormField>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 pt-1 border-t">
        <Button
          onClick={() => void handleSubmit()}
          disabled={saving || !name}
          className="min-h-[44px]"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin me-1.5" />}
          {saving ? "שומר..." : isNew ? "צור פופאפ" : "שמור"}
        </Button>
        <Button variant="outline" onClick={() => navigate("/popups")} className="min-h-[44px]">
          ביטול
        </Button>
      </div>
    </div>
  );
}
